import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Folder, DotsThreeVertical, PencilSimple, Trash, ArrowRight } from '@phosphor-icons/react';
import type { DocumentNodeWithDetailsDto } from '@monorepo/shared';
import { DocumentNodeType } from '@monorepo/shared';
import { Tooltip } from '../ui/Tooltip';

interface FolderCardProps {
  node: DocumentNodeWithDetailsDto;
  childrenCount?: number;
  canRename?: boolean;
  canDelete?: boolean;
  onOpen: (nodeId: string) => void;
  onRename: (node: DocumentNodeWithDetailsDto) => void;
  onDelete: (node: DocumentNodeWithDetailsDto) => void;
}

export const FolderCard = ({
  node,
  childrenCount = 0,
  canRename = false,
  canDelete = false,
  onOpen,
  onRename,
  onDelete,
}: FolderCardProps) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isMenuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setIsMenuOpen(!isMenuOpen);
  };

  const handleAction = (action: 'rename' | 'delete') => {
    setIsMenuOpen(false);
    if (action === 'rename') {
      onRename(node);
    } else if (action === 'delete') {
      onDelete(node);
    }
  };

  const isFolder = node.type === DocumentNodeType.FOLDER;

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
      onClick={() => onOpen(node.id)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="flex-shrink-0 mt-0.5">
            <Folder size={24} className="text-primary" weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <Tooltip content={node.name} className="relative block min-w-0">
              <h3 className="font-medium text-gray-900 truncate">{node.name}</h3>
            </Tooltip>
            <p className="text-sm text-gray-500 mt-1">
              {isFolder
                ? t('documents.folder.subfolders_count', { count: childrenCount })
                : t('documents.folder.documents_count', { count: childrenCount })}
            </p>
          </div>
        </div>

        <div className="relative flex-shrink-0">
          <button
            ref={buttonRef}
            onClick={handleMenuToggle}
            className="p-1 rounded hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <DotsThreeVertical size={20} className="text-gray-500" />
          </button>

          {isMenuOpen && (
            <div
              ref={menuRef}
              className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[160px]"
              style={{
                top: `${menuPosition.top}px`,
                right: `${menuPosition.right}px`,
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(node.id);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <ArrowRight size={16} />
                {t('documents.actions.open')}
              </button>
              {canRename && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction('rename');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <PencilSimple size={16} />
                  {t('documents.actions.rename')}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAction('delete');
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <Trash size={16} />
                  {t('documents.actions.delete')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
